/**
 * The Archive — where recovered Collapse Keys are read, and re-read.
 *
 * One instance of the shared collection archetype (collectionView.js), not a
 * bespoke screen. A key arrives as a raid message that scrolls away; this is
 * the surface that makes it permanent, which is the requirement
 * `docs/archive/BreachLayer.md` recorded as "neither a toast nor a modal".
 *
 * Grouped by faction rather than by tier, because the three sets are three
 * arguments and reading one straight through is the point. Every faction's set
 * is shown, including factions the account has never played — the shape of what
 * is missing is what makes the collection legible, and it is also what tells a
 * player that the other two theories exist at all.
 *
 * The header states the seven-key ceiling plainly. Keys 8-10 are authored and
 * land at Tier 4 with L4; saying so is better than a set that silently stops at
 * seven and reads as unfinished content.
 */

import * as config from '../constants.js';
import { collectionHtml } from './collectionView.js';
import { keysForFaction, ensureCollapseKeyState, collapseKeyProgress } from './collapseKeys-core.js';

const FACTION_ACCENTS = {
  dominion: '#7cc4ff',
  wilders: '#7bd88f',
  veil: '#b18cff',
};

function keyEntry(key, owned) {
  return {
    id: key.id,
    owned,
    marker: key.code,
    icon: '🗝️',
    title: key.name,
    subtitle: `Tier ${key.tier} · ${key.recoveredFrom}`,
    sections: [
      { label: `Fragment — ${key.source}`, text: key.fragment, quote: true },
      { label: 'Interpretation', text: key.interpretation },
    ],
  };
}

export function renderArchiveTab() {
  const container = document.getElementById('archive-body');
  if (!container) return;

  const owned = ensureCollapseKeyState();
  const progress = collapseKeyProgress();
  const totalOwned = Object.values(progress).reduce((n, p) => n + p.owned, 0);
  const totalKeys = Object.values(progress).reduce((n, p) => n + p.total, 0);

  const groups = Object.keys(config.factionSkills || {}).map(factionKey => {
    const faction = config.factionSkills[factionKey];
    return {
      id: factionKey,
      label: `${faction.name} — Collapse Keys`,
      icon: faction.icon,
      accent: FACTION_ACCENTS[factionKey] || '#9ca3af',
      blurb: faction.doctrine,
      entries: keysForFaction(factionKey).map(k => keyEntry(k, owned.includes(k.id))),
    };
  });

  const summary = document.getElementById('archive-summary');
  if (summary) {
    summary.textContent = totalOwned === 0
      ? 'Nothing recovered yet. Keys surface in the field — raid, and your faction will start filing them here.'
      : `${totalOwned} of ${totalKeys} recovered. A key is filed to the account, not to a character: promotion and founding never take one back.`;
  }

  container.innerHTML = collectionHtml({
    groups,
    empty: 'No Collapse Keys have been authored yet.',
  });
}
